/* eslint-disable @typescript-eslint/no-explicit-any */
import { Chip, TextField } from '@client/new/components/basic';

import MediaUpload, { MediaInfo } from '@client/new/components/MediaUpload';
import { faImagePolaroidUser, faVideo } from '@fortawesome/pro-light-svg-icons';
import { ColorPicker } from '@client/components/common/inputs/ColorPicker';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import ajax from '@client/components/ajax'; // NOTE: old
import toast from 'react-hot-toast';
import PillSelect, {
  PillProps,
} from '@client/components/collections/PillSelect';
import notLiveProfileSchema, {
  liveProfileSchema,
  NotLiveProfile,
} from './schema';
import { Label, PROFILE_KEYWORDS } from './helpers';
import { Preview } from './MobilePreview';
import {
  useGetProfileQuery,
  IsCustomUrlAvailableDocument,
  IsCustomUrlAvailableQuery,
  GetProfileQuery,
  useSetCustomUrlMutation,
} from '../../../../../generated/graphql';
import Alert from '@mui/material/Alert';
import { PrimaryButton } from '@client/new/components/button';
import { useInfluencer } from '../../../hooks/use-influencer';
import { muiForm } from '@client/new/helpers/forms';
import { ChangeEvent, useEffect, useState, PropsWithChildren } from 'react';
import { LiveTextField } from '@client/new/components/basic/LiveTextField';
import { apolloClient } from '@client/api/graphql/client';
import * as enums from '@playbook-technologies/common-enums';
import { emitGAEvent } from '../../../services/ga4';
import { trackEvent } from '@services/tracking';
import { RHFSelect } from '@client/new/components/rhfSelect';
import { Option } from '@client/new/helpers/types';
import { Tag } from '@design-system/components/tag/tag';
import { sprinkles } from '@design-system/sprinkles.css';
import { InfoBar } from '@design-system/components/statusBar/StatusBar';
import { Typography } from '@design-system/components/typography/typography';
import { Col, FormLabel, Row } from '@design-system/components/grid/grid';
import {
  faInstagram,
  faTiktok,
  faYoutube,
} from '@fortawesome/free-brands-svg-icons';
import SuperTooltip from '@client/new/components/SuperTooltip/SuperTooltip';
import { SuperTooltipTemplateId } from '@client/new/components/SuperTooltip/generated';

const withData = (Component: any) => (props: any) => {
  const { influencer } = useInfluencer();
  const influencerId = props.params.influencerId || influencer?.id;
  const shouldValidateReadiness = Object.hasOwn(
    props.location.query,
    'strictForm',
  );

  const { data, loading, error } = useGetProfileQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      id: Number(influencerId),
    },
  });

  if (!data) return null;

  if (error) {
    // TODO: send to Sentry
    return (
      <div className="w-full h-full grid items-center justify-center bg-white text-red-500">
        <div className="text-center text-xl">
          <Alert severity="warning">Unexpected Error!</Alert>
          <Alert severity="error" className="mt-2">
            {String(error)}
          </Alert>
          <Alert severity="info" className="mt-2">
            Please contact{' '}
            <a href={`mailto:creatorsupport@playbookapp.io `}>
              support@playbookapp.io
            </a>
          </Alert>
        </div>
      </div>
    );
  }

  if (loading) return null;

  return (
    <Component
      profile={data?.creator}
      influencerId={Number(influencerId)}
      shouldValidateReadiness={shouldValidateReadiness}
    />
  );
};

export default withData(ProfilePage);

const normalize = (v: string) =>
  v
    .trim()
    .replace(/[\W_]+/g, '-')
    .toLowerCase();

function profileToCustomUrl(profile: GetProfileQuery['creator']) {
  if (!profile) return '';
  return (
    profile.customURL || normalize(`${profile.firstName}-${profile.lastName}`)
  );
}

const FormSection = ({
  title,
  children,
  ...p
}: PropsWithChildren<{ title?: string; id?: string }>) => (
  <Col className={title ? sprinkles({ marginTop: '2xl' }) : ''} gap="xl" {...p}>
    {title ? (
      <Typography variant="title" size="large">
        {title}
      </Typography>
    ) : null}
    {children}
  </Col>
);

type PageProps = {
  profile: GetProfileQuery['creator'];
  influencerId: number;
  shouldValidateReadiness: boolean;
};

const toCustomURLValidator =
  (prevCustomURL: string) =>
  async (customURL: string): Promise<[boolean, string?]> => {
    const result = await apolloClient.query<IsCustomUrlAvailableQuery>({
      query: IsCustomUrlAvailableDocument,
      variables: { customUrl: customURL },
    });

    const isAvailable =
      result.data.isCustomUrlAvailable || customURL === prevCustomURL;

    return [
      isAvailable,
      isAvailable ? '' : 'This name is already in use. Please change it.',
    ];
  };

const FormLabelSuperTooltip = (
  p: PropsWithChildren<{
    templateId: SuperTooltipTemplateId;
    required?: boolean;
  }>,
) => {
  return (
    <Row gap="m">
      <FormLabel required={p.required}>{p.children}</FormLabel>
      <SuperTooltip templateId={p.templateId} />
    </Row>
  );
};

function ProfilePage(props: PageProps) {
  const {
    profile: existingProfile,
    influencerId,
    shouldValidateReadiness,
  } = props;

  const [customURL, setCustomUrl] = useState(
    profileToCustomUrl(existingProfile),
  );

  const handleCustomUrlChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (inputValue.length > 30) return;
    if (/^[a-zA-Z0-9-_]*$/.test(inputValue)) setCustomUrl(inputValue);
  };

  const [setCustomUrlMutation] = useSetCustomUrlMutation({
    variables: { customURL },
  });

  const isLive = existingProfile?.mode === enums.Influencer.Mode.Live.value;
  const schema =
    isLive || shouldValidateReadiness
      ? liveProfileSchema
      : notLiveProfileSchema;

  const isRequired = (
    fieldName: keyof (typeof liveProfileSchema)['shape'],
  ): boolean => {
    const fieldSchema = liveProfileSchema.shape[fieldName];
    return !fieldSchema.isOptional();
  };
  const {
    register,
    setValue,
    handleSubmit,
    watch,
    formState,
    control,
    trigger,
  } = muiForm(
    useForm<NotLiveProfile>({
      defaultValues: {
        mediaBanner: null,
        mediaWelcome: null,
        customColorSchema: { primaryColor: '000000' },
        gender: null,
        ...(defined(existingProfile) as any),
      },
      resolver: zodResolver(schema),
    }),
  );

  const [isSaving, setIsSaving] = useState(false);

  const profileForm = watch();

  useEffect(() => {
    if (shouldValidateReadiness) {
      trigger();
    }
  }, []);

  const primaryColor = watch('customColorSchema.primaryColor') || '000000';

  const onSave = handleSubmit((data) => {
    const shouldUpdateCustomUrl =
      customURL !== profileToCustomUrl(existingProfile);

    if (shouldUpdateCustomUrl) {
      setCustomUrlMutation()
        .then(() => toast.success('Channel url updated!'))
        .catch((err) => toast.error('Error saving custom URL: ' + err.message));
    }

    setIsSaving(true);
    ajax
      .upsertInfluencer({ ...(data as any), id: influencerId })
      .then(() => {
        // navigate(PATHS.HOME);
        toast.success('Profile saved!');
        emitGAEvent({
          type: 'Profile Set Up Completed',
          payload: { influencer_id: influencerId },
        });

        trackEvent({
          type: 'Profile Set Up Completed',
        });
      })
      .catch((err) => {
        toast.error('Error saving profile: ' + err.message);
      })
      .finally(() => {
        setIsSaving(false);
      });

    ajax
      .upsertCustomColorSchema(
        influencerId,
        (data as any)?.customColorSchema as any,
      )
      .catch((err) => {
        toast.error('Error saving color schema: ' + err.message);
      });
  });

  const onValidateCustomURL = toCustomURLValidator(
    existingProfile?.customURL || '',
  );

  const camelCaseToTitle = (str: string) =>
    str.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());
  const validationMessage = Object.entries(formState.errors)
    .map(([name, _error]) => `${camelCaseToTitle(name)} is invalid`)
    .join('\n');

  const genderOptions: Option<NotLiveProfile['gender']>[] = [
    {
      value: null,
      label: profileForm.gender === null ? 'Not selected' : 'Prefer not to say',
    },
    { value: 'M', label: 'Male' },
    { value: 'F', label: 'Female' },
  ];

  useEffect(() => {
    trackEvent({
      type: 'Legacy settings route visited',
      payload: {
        Path: '/creator/account/profile',
      },
    });
  }, []);

  return (
    <div className="min-h-screen w-full">
      <div
        className="bg-white rounded-xl p-8 mt-7 "
        style={{ maxWidth: '720px' }}
      >
        <div
          className={sprinkles({
            justifyContent: 'space-between',
            flexDirection: 'row',
            display: 'flex',
            alignItems: 'center',
            marginBottom: 'l',
          })}
        >
          <Typography variant="title" size="large">
            General Info
          </Typography>
          <PrimaryButton
            id="save-profile-btn"
            onClick={onSave}
            className="w-[110px] h-[40px]"
            isLoading={isSaving}
          >
            Save
          </PrimaryButton>
        </div>
        <div className="xl:flex data-block">
          <div
            id="form-container"
            className="w-full md:min-w-[650px] max-w-4xl flex-1"
          >
            <FormSection id="general">
              <InfoBar text="Fields marked with a red asterisk are required to publish any content." />
              {validationMessage && (
                <Alert severity="warning" className="whitespace-pre-wrap mb-5">
                  {validationMessage}
                </Alert>
              )}
              <Row gap="xl">
                <Col>
                  <FormLabel required={isRequired('firstName')}>
                    First Name
                  </FormLabel>
                  <TextField {...register('firstName')} className="w-full" />
                </Col>

                <Col>
                  <FormLabel required={isRequired('lastName')}>
                    Last Name
                  </FormLabel>
                  <TextField {...register('lastName')} className="w-full" />
                </Col>
              </Row>

              <Col>
                <FormLabel required>Playbook app channel URL</FormLabel>
                <LiveTextField
                  name="customURL"
                  required
                  // label="Playbook app Channel URL"
                  className="mt-4 w-full"
                  value={customURL}
                  onChange={handleCustomUrlChange}
                  startAdornment="https://my.playbookapp.io/"
                  // helperText="This text is editable until your profile is live. After that, you will be unable to make changes."
                  isValid={onValidateCustomURL}
                  disabled={
                    existingProfile?.mode ===
                      enums.Influencer.Mode.Live.value ||
                    existingProfile?.mode === enums.Influencer.Mode.RunOff.value
                  }
                />
                <Typography size="small" color="N-80">
                  This text is editable until your profile is live. After that,
                  you will be unable to make changes.
                </Typography>
              </Col>

              <Col>
                <FormLabel
                  hideTooltip={isLive}
                  tooltip="You’ll become searchable on the Playbook app after you publish your first product. Before publishing, you have to set up your profile & Stripe."
                >
                  Profile Status
                </FormLabel>

                <Tag
                  readonly
                  label={isLive ? 'Live' : 'Not live'}
                  className={sprinkles({ bg: isLive ? 'G-100' : 'D-40' })}
                />
              </Col>

              <Col id="branding">
                <FormLabel tooltip="Strengthen your brand with a consistent color palette to make your content instantly recognizable and memorable to your audience.">
                  Channel color
                </FormLabel>
                <ColorPicker
                  value={primaryColor ? '#' + primaryColor : '#000'}
                  onChange={(c) => {
                    setValue(
                      'customColorSchema.primaryColor',
                      c.slice(1).toUpperCase(),
                    );

                    trackEvent({
                      type: 'Brand Color Submitted',
                      payload: {},
                    });
                  }}
                  showColorBox
                />
              </Col>

              <Col>
                <FormLabel>Gender</FormLabel>
                <RHFSelect<NotLiveProfile>
                  name="gender"
                  control={control}
                  options={genderOptions}
                />
              </Col>
            </FormSection>

            <FormSection id="social-channels" title="Social media">
              <Row gap="xl">
                <Col>
                  <FormLabel
                    icon={faInstagram}
                    required={isRequired('profileInstagram')}
                  >
                    Instagram
                  </FormLabel>
                  <TextField
                    {...register('profileInstagram')}
                    placeholder="Without @"
                    className="w-full"
                  />
                </Col>
                <Col>
                  <FormLabel icon={faTiktok}>TikTok</FormLabel>
                  <TextField
                    {...register('profileTiktok')}
                    placeholder="Without @"
                    className="w-full"
                  />
                </Col>
                <Col>
                  <FormLabel icon={faYoutube}>YouTube</FormLabel>
                  <TextField
                    {...register('profileYoutubeChannel')}
                    placeholder="https://www.youtube.com/channel/your_channel"
                    className="w-full"
                  />
                </Col>
              </Row>
            </FormSection>

            <FormSection id="about-you" title="About me">
              <Col>
                <FormLabelSuperTooltip
                  templateId="profile-title"
                  required={isRequired('title')}
                >
                  Title
                </FormLabelSuperTooltip>
                <TextField className="w-full" {...register('title')} />
              </Col>

              <Col>
                <FormLabelSuperTooltip templateId="profile-story">
                  Story
                </FormLabelSuperTooltip>
                <TextField
                  multiline
                  minRows={5}
                  className=" bg-white w-full"
                  inputProps={{ maxLength: 800 }}
                  {...register('messageProfile')}
                />
              </Col>

              <Col>
                <FormLabelSuperTooltip templateId="accomplishments">
                  Accomplishments
                </FormLabelSuperTooltip>
                <TextField
                  multiline
                  minRows={5}
                  className=" bg-white w-full"
                  inputProps={{ maxLength: 500 }}
                  {...register('accomplishments')}
                />
              </Col>
            </FormSection>

            <FormSection title="Media" id="media">
              <Row>
                <Col>
                  <FormLabelSuperTooltip
                    required={isRequired('mediaAvatar')}
                    templateId={'profile-avatar-image'}
                  >
                    Avatar Image
                  </FormLabelSuperTooltip>

                  <MediaUpload
                    creatorId={influencerId}
                    className="aspect-[1/1] w-[94px] rounded-full bg-gray-100"
                    accept={['image/png', 'image/jpeg']}
                    value={watch('mediaAvatar') || null}
                    onChange={(metadata) => {
                      setValue('mediaAvatar', metadata as any);
                      trackEvent({
                        type: 'Avatar Submitted',
                        payload: {},
                      });
                    }}
                    id="avatar"
                  >
                    <MediaInfo title="Avatar" subtitle="100x100px" />
                  </MediaUpload>
                </Col>
              </Row>

              <Row gap="xl">
                <Col>
                  <FormLabelSuperTooltip
                    templateId={'cover-image'}
                    required={isRequired('mediaCover')}
                  >
                    Cover Image
                  </FormLabelSuperTooltip>
                  <MediaUpload
                    creatorId={influencerId}
                    id="cover-photo"
                    className="aspect-[1/1] h-[180px] md:h-[230px] bg-gray-100"
                    accept={['image/png', 'image/jpeg']}
                    showTimeLeft
                    value={watch('mediaCover') || null}
                    onChange={(metadata) => {
                      setValue('mediaCover', metadata as any);
                      trackEvent({
                        type: 'Cover Photo Submitted',
                        payload: {},
                      });
                    }}
                  >
                    <MediaInfo
                      icon={faImagePolaroidUser as any}
                      title="Upload Image"
                      subtitle="Recommended 1080x1080px"
                    />
                  </MediaUpload>
                </Col>

                <Col>
                  <FormLabelSuperTooltip
                    templateId="profile-banner"
                    required={isRequired('mediaBanner')}
                  >
                    Profile Banner
                  </FormLabelSuperTooltip>
                  <MediaUpload
                    creatorId={influencerId}
                    id="mobile-banner"
                    className="h-[94px] aspect-[1125/420] bg-gray-100"
                    accept={['image/png', 'image/jpeg']}
                    crop
                    aspectRatio={[1125, 420]}
                    value={watch('mediaBanner') || null}
                    onChange={(metadata) => {
                      setValue('mediaBanner', metadata as any);
                      trackEvent({
                        type: 'Banner Submitted',
                        payload: {},
                      });
                    }}
                  >
                    <MediaInfo
                      title="Upload Banner"
                      subtitle="cropped to 1125x420px"
                    />
                  </MediaUpload>
                </Col>
                <Col>
                  <FormLabelSuperTooltip templateId="intro-video">
                    Introduction Video
                  </FormLabelSuperTooltip>
                  <MediaUpload
                    creatorId={influencerId}
                    id="welcome-video"
                    className="aspect-[9/16] h-[180px] md:h-[230px] bg-gray-100"
                    accept={['video/mp4']}
                    showTimeLeft
                    aspectRatio={[9, 16]}
                    value={watch('mediaWelcome') || null}
                    onChange={(metadata) => {
                      setValue('mediaWelcome', metadata as any);
                      trackEvent({
                        type: 'Welcome Video Uploaded',
                        payload: {},
                      });
                    }}
                  >
                    <MediaInfo
                      icon={faVideo as any}
                      title="Upload mp4 video"
                      subtitle="9:16 (portrait)"
                    />
                  </MediaUpload>
                </Col>
              </Row>
            </FormSection>

            <FormSection title="Discovery">
              <Typography variant="bodyLarge">
                Select up to 7 keywords that best describe your channel and type
                of content offered. It makes your content easier to find.
              </Typography>
              <Label title="">
                <PillSelect
                  values={watch('keywords')}
                  options={PROFILE_KEYWORDS.map((name) => ({
                    name,
                    value: name,
                  }))}
                  onChange={(array) => {
                    setValue('keywords', array);

                    trackEvent({
                      type: 'Onboarding Tags Submitted',
                      payload: {},
                    });
                  }}
                  gap={2}
                  Pill={KeywordsPill}
                  limit={7}
                  multi
                  label=""
                />
              </Label>
            </FormSection>
          </div>
          <div className="hidden xl:block ml-12 mt-24">
            <Preview profile={profileForm} />
          </div>
        </div>
      </div>
    </div>
  );
}

function KeywordsPill({ o, isActive, onClick }: PillProps) {
  return (
    <Chip
      onClick={onClick}
      key={o.value}
      label={o.name}
      variant={isActive ? 'filled' : 'outlined'}
      onDelete={isActive ? onClick : undefined}
    />
  );
}

// helpers
function defined<T>(o: T): Partial<T> {
  const r = {} as Partial<T>;
  for (const k in o) {
    if (o[k] !== undefined && o[k] !== null) {
      r[k] = o[k];
    }
  }
  return r;
}
